import React from 'react';
import { styles } from './ContactBlock.css';
import CrazyFont from '@jbhi-fi/lanyard-ui/font/CrazyFont';
import { Icon } from '@jbhi-fi/lanyard-ui/components/Icon';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { TrackingProvider, useTrackingContext } from '../Tracking';
import { useNavigate } from '../PlatformFunctionality';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { useIdPrefix } from '../IdPrefix';
import { ContactBlockProps, ContactButtonProps } from './types';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { MarkdownBlock } from '../MarkdownBlock';

const ContactButton = ({ icon, iconProps, text, link, onClickHandler, contentPalette, testId }: ContactButtonProps) => {
  const { tracker } = useTrackingContext();
  const navigate = useNavigate();

  const handleOnClick = () => {
    tracker.trackClick({ label: text, extraData: { moduleName: 'Contact block' } });
    if (link) {
      navigate(link);
    } else if (onClickHandler) {
      onClickHandler();
    }
  };

  return (
    <Button
      type="linkWithIcon"
      className={styles.actionButton}
      style={{ color: contentPalette?.text }}
      iconPosition="left"
      onClick={handleOnClick}
      icon={
        <span className={styles.icon}>
          <Icon {...iconProps} name={icon} />
        </span>
      }
      data-testid={testId}
    >
      {text}
    </Button>
  );
};

export const ContactBlock = ({
  heading,
  subText,
  chatText,
  chatUrl,
  chatOnClick,
  shouldHideCallUs,
  callText,
  callUrl,
  callOnClick,
  contentPalette,
  variant = 'default',
  idComponent,
  salesLineHours,
  margin,
  shouldShowExclusionText = true,
  testId,
  disclaimerText,
}: ContactBlockProps) => {
  const isDesktop = useBreakpoint('lg', 'up');
  const suffixId = idComponent ? `${idComponent}-` : '';
  const chatId = useIdPrefix(`${suffixId}chat`);
  const callId = useIdPrefix(`${suffixId}call`);

  return (
    <TrackingProvider shouldInheritFromParentContexts group={heading}>
      <div
        className={classNames(styles.container({ variant }), styles.margin({ margin }))}
        style={{ backgroundColor: contentPalette?.background }}
        id={useIdPrefix(`${suffixId}contact-block`)}
        data-testid={testId}
      >
        <div className={styles.grid({ variant })}>
          {subText ? (
            <CrazyFont className={styles.subText({ variant })} style={{ color: contentPalette?.text }}>
              {subText}
            </CrazyFont>
          ) : null}
          {heading ? (
            <div
              className={styles.headingBorder}
              style={
                contentPalette?.heading
                  ? { backgroundColor: contentPalette.heading, backgroundImage: 'unset' }
                  : undefined
              }
            >
              <div
                className={styles.headingContent}
                style={contentPalette?.background ? { backgroundColor: contentPalette.background } : undefined}
              >
                <div
                  className={styles.headingWrapper({ variant })}
                  style={contentPalette?.heading ? { color: contentPalette?.heading } : undefined}
                >
                  {isDesktop ? (
                    <CrazyFont className={styles.heading({ variant })}>Ask for a JB Deal!</CrazyFont>
                  ) : (
                    <>
                      <CrazyFont className={styles.headingIntro({ variant })}>Ask for a</CrazyFont>
                      <CrazyFont
                        as={isDesktop && variant === 'default' ? 'h2' : 'h3'}
                        className={styles.heading({ variant })}
                      >
                        JB Deal!
                      </CrazyFont>
                    </>
                  )}
                  <p
                    className={styles.headingBorderLabel({ variant })}
                    style={{
                      backgroundImage: 'unset',
                      ...(contentPalette?.heading ? { backgroundColor: contentPalette.heading } : {}),
                    }}
                  >
                    <CalloutFont style={{ color: contentPalette?.background ?? palette?.primary?.main }}>
                      Instore | Online
                    </CalloutFont>
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          {shouldShowExclusionText && disclaimerText ? (
            <div
              className={styles.exclusions}
              style={{ color: contentPalette?.text ? contentPalette.text : undefined }}
            >
              <MarkdownBlock>{disclaimerText}</MarkdownBlock>
            </div>
          ) : null}
          <div
            className={classNames(
              styles.actionWrapper({ variant }),
              !shouldHideCallUs ? styles.actionWrapperNarrow : ''
            )}
          >
            <ContactButton
              icon="Chat"
              text={chatText}
              link={chatUrl}
              onClickHandler={chatOnClick}
              contentPalette={contentPalette}
              testId={chatId}
            />
            {!shouldHideCallUs && callText ? (
              <ContactButton
                icon="Phone"
                iconProps={{ style: { marginRight: '-0.3em' } }}
                text={callText}
                link={callUrl}
                onClickHandler={callOnClick}
                contentPalette={contentPalette}
                testId={callId}
              />
            ) : null}
            {!shouldHideCallUs && salesLineHours ? (
              <div
                className={styles.salesLineHours}
                style={{ color: contentPalette?.text ? contentPalette.text : undefined }}
              >
                {salesLineHours}
              </div>
            ) : (
              <div
                className={styles.salesLineTime}
                style={{ color: contentPalette?.text ? contentPalette.text : undefined }}
              >
                {salesLineHours}
              </div>
            )}
          </div>
        </div>
      </div>
    </TrackingProvider>
  );
};
